import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { Mic, MicOff, Volume2, VolumeX, HelpCircle, X, Settings } from 'lucide-react';

// Voice command definitions
const VOICE_COMMANDS = {
  navigation: [
    { command: "go to dashboard", action: "/dashboard", aliases: ["open dashboard", "show dashboard"] },
    { command: "go to audits", action: "/audits", aliases: ["open audits", "show audits"] },
    { command: "go to observations", action: "/observations", aliases: ["open observations"] },
    { command: "go to training", action: "/training", aliases: ["open training", "show training"] },
    { command: "go to analytics", action: "/analytics-intelligence", aliases: ["open analytics", "show analytics"] },
    { command: "go to safety ops", action: "/safety-ops", aliases: ["open safety operations"] },
    { command: "go to monitoring", action: "/monitoring", aliases: ["open monitoring hub"] },
    { command: "go to reports", action: "/reports", aliases: ["open reports", "show reports"] },
    { command: "go to gamification", action: "/gamification", aliases: ["show achievements", "open achievements"] },
  ],
  actions: [
    { command: "start audit", action: "START_AUDIT", aliases: ["begin audit", "new audit"] },
    { command: "report hazard", action: "REPORT_HAZARD", aliases: ["report observation", "new observation"] },
    { command: "emergency stop", action: "EMERGENCY", aliases: ["emergency", "panic", "help"] },
    { command: "take photo", action: "CAPTURE_PHOTO", aliases: ["capture image", "photograph"] },
    { command: "start recording", action: "START_RECORDING", aliases: ["record", "begin recording"] },
    { command: "stop recording", action: "STOP_RECORDING", aliases: ["end recording", "finish recording"] },
  ],
  system: [
    { command: "show commands", action: "SHOW_HELP", aliases: ["help", "what can you do", "voice commands"] },
    { command: "stop listening", action: "STOP_LISTENING", aliases: ["pause", "mute", "stop"] },
    { command: "start listening", action: "START_LISTENING", aliases: ["resume", "unmute", "listen"] },
  ]
};

const VoiceCommands = ({ onAction, isEnabled = true }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastCommand, setLastCommand] = useState('');
  const recognitionRef = useRef(null);
  const synthRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = 'en-US';
        
        recognition.onstart = () => {
          setIsListening(true);
        };
        
        recognition.onend = () => {
          setIsListening(false);
          // Restart if should be listening
          if (recognitionRef.current?.shouldListen) {
            try {
              recognition.start();
            } catch (e) {
              // Already started
            }
          }
        };
        
        recognition.onresult = (event) => {
          let interimTranscript = '';
          let finalTranscript = '';
          
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const transcript = event.results[i][0].transcript;
            if (event.results[i].isFinal) {
              finalTranscript += transcript;
            } else {
              interimTranscript += transcript;
            }
          }
          
          setTranscript(interimTranscript || finalTranscript);
          
          if (finalTranscript) {
            processCommand(finalTranscript.toLowerCase().trim());
          }
        };
        
        recognition.onerror = (event) => {
          if (event.error === 'not-allowed') {
            setIsSupported(false);
            toast({
              title: "Microphone Access Denied",
              description: "Please enable microphone access to use voice commands",
              variant: "destructive"
            });
          }
        };
        
        recognitionRef.current = recognition;
        recognitionRef.current.shouldListen = false;
      } else {
        setIsSupported(false);
      }
      
      // Initialize speech synthesis
      synthRef.current = window.speechSynthesis;
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.shouldListen = false;
        try {
          recognitionRef.current.stop();
        } catch (e) {}
      }
    };
  }, []);

  // Speak text
  const speak = useCallback((text) => {
    if (synthRef.current) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      synthRef.current.speak(utterance);
    }
  }, []);

  // Process voice command
  const processCommand = useCallback((text) => {
    setLastCommand(text);
    
    // Check all command categories
    for (const category of Object.values(VOICE_COMMANDS)) {
      for (const cmd of category) {
        const allCommands = [cmd.command, ...(cmd.aliases || [])];
        
        for (const phrase of allCommands) {
          if (text.includes(phrase)) {
            executeCommand(cmd.action, cmd.command);
            return;
          }
        }
      }
    }
    
    // No command matched
    toast({
      title: "Command Not Recognized",
      description: `"${text}" - Say "show commands" for help`,
      duration: 3000
    });
  }, [navigate, toast, speak, onAction]);

  // Execute command
  const executeCommand = useCallback((action, commandName) => {
    // Navigation commands
    if (action.startsWith('/')) {
      speak(`Navigating to ${commandName.replace('go to ', '')}`);
      navigate(action);
      toast({
        title: "Voice Command",
        description: `Navigating to ${commandName.replace('go to ', '')}`,
      });
      return;
    }
    
    // Action commands
    switch (action) {
      case 'SHOW_HELP':
        setShowHelp(true);
        speak("Showing voice commands");
        break;
        
      case 'STOP_LISTENING':
        stopListening();
        speak("Voice commands paused");
        break;
        
      case 'START_LISTENING':
        startListening();
        speak("Voice commands active");
        break;
        
      case 'EMERGENCY':
        speak("Emergency alert activated");
        toast({
          title: "🚨 Emergency Alert",
          description: "Emergency response initiated",
          variant: "destructive",
          duration: 10000
        });
        if (onAction) onAction('EMERGENCY');
        break;
        
      case 'START_AUDIT':
        speak("Starting new audit");
        navigate('/audits');
        if (onAction) onAction('START_AUDIT');
        break;
        
      case 'REPORT_HAZARD':
        speak("Opening hazard report");
        navigate('/observations');
        if (onAction) onAction('REPORT_HAZARD');
        break;
        
      case 'CAPTURE_PHOTO':
        speak("Opening camera");
        if (onAction) onAction('CAPTURE_PHOTO');
        break;
        
      case 'START_RECORDING':
        speak("Recording started");
        if (onAction) onAction('START_RECORDING');
        break;
        
      case 'STOP_RECORDING':
        speak("Recording stopped");
        if (onAction) onAction('STOP_RECORDING');
        break;
        
      default:
        if (onAction) onAction(action);
    }
    
    toast({
      title: "Voice Command",
      description: `Executed: ${commandName}`,
    });
  }, [navigate, toast, speak, onAction]);

  // Start/stop listening
  const startListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.shouldListen = true;
      try {
        recognitionRef.current.start();
      } catch (e) {
        // Already started
      }
    }
  }, []);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.shouldListen = false;
      try {
        recognitionRef.current.stop();
      } catch (e) {}
    }
  }, []);

  const toggleListening = useCallback(() => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [isListening, startListening, stopListening]);

  if (!isSupported || !isEnabled) {
    return null;
  }

  return (
    <>
      {/* Voice Control Button */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
        {/* Transcript display */}
        {isListening && transcript && (
          <div className="bg-white shadow-lg rounded-lg px-4 py-2 max-w-xs animate-pulse">
            <p className="text-sm text-gray-600">
              <span className="text-blue-600">🎤</span> {transcript}
            </p>
          </div>
        )}
        
        {/* Last command */}
        {lastCommand && !transcript && (
          <div className="bg-gray-100 rounded-lg px-3 py-1 text-xs text-gray-500">
            Last: "{lastCommand}"
          </div>
        )}
        
        {/* Control buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="icon"
            className="rounded-full w-10 h-10 bg-white shadow-lg"
            onClick={() => setShowHelp(true)}
          >
            <HelpCircle className="w-5 h-5" />
          </Button>
          
          <Button
            size="icon"
            className={`rounded-full w-14 h-14 shadow-lg transition-all ${
              isListening 
                ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
            onClick={toggleListening}
          >
            {isListening ? (
              <Mic className="w-6 h-6 text-white" />
            ) : (
              <MicOff className="w-6 h-6 text-white" />
            )}
          </Button>
          
          {isSpeaking && (
            <Button
              variant="outline"
              size="icon"
              className="rounded-full w-10 h-10 bg-white shadow-lg animate-pulse"
              onClick={() => synthRef.current?.cancel()}
            >
              <Volume2 className="w-5 h-5 text-blue-600" />
            </Button>
          )}
        </div>
        
        {/* Status indicator */}
        {isListening && (
          <Badge className="bg-red-500 animate-pulse">
            🎤 Listening...
          </Badge>
        )}
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Mic className="w-6 h-6 text-blue-600" />
                  Voice Commands
                </h2>
                <Button variant="ghost" size="icon" onClick={() => setShowHelp(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              
              <p className="text-gray-600 text-sm mb-4">
                Say any of these commands to navigate or perform actions hands-free.
              </p>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-blue-600 mb-2">🧭 Navigation</h3>
                  <div className="space-y-1">
                    {VOICE_COMMANDS.navigation.map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b">
                        <span className="font-medium">"{cmd.command}"</span>
                        <span className="text-gray-500">{cmd.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-green-600 mb-2">⚡ Actions</h3>
                  <div className="space-y-1">
                    {VOICE_COMMANDS.actions.map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b">
                        <span className="font-medium">"{cmd.command}"</span>
                        <span className="text-gray-500 text-xs">{cmd.aliases?.[0] && `or "${cmd.aliases[0]}"`}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold text-purple-600 mb-2">🔧 System</h3>
                  <div className="space-y-1">
                    {VOICE_COMMANDS.system.map((cmd, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b">
                        <span className="font-medium">"{cmd.command}"</span>
                        <span className="text-gray-500 text-xs">{cmd.aliases?.[0] && `or "${cmd.aliases[0]}"`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Click the microphone button to start listening, 
                  or say "start listening" at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VoiceCommands;
